import CloseIcon from '@mui/icons-material/Close';
import { DialogActions, DialogContent, DialogTitle, Fade, Stack, useMediaQuery } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

// eslint-disable-next-line react/display-name
const Transition = React.forwardRef((props, ref) => {
  return <Fade ref={ref} {...props} />;
});

function ResponsiveDialog({
  title,
  open,
  fullScreen,
  submitText,
  submitColor = 'primary',
  cancelText,
  hideCancel = false,
  sx = [],
  children,
  extraActions,
  maxWidth = 'md',
  canSubmit,
  PaperProps,
  ContentProps = {},
  ActionsStyleProps = {},
  onClose,
  onCancel,
  onSubmit,
}) {
  const { t } = useTranslation();

  const [isSubmitting, setIsSubmitting] = useState(false);
  // Phone-width dialogs go full-screen so long forms scroll instead of
  // floating in a cramped modal. An explicit `fullScreen` prop (either
  // value) always wins over the viewport default.
  const isPhone = useMediaQuery(theme => theme.breakpoints.down('sm'));
  const effectiveFullScreen = fullScreen ?? isPhone;

  let realSubmitText = submitText;
  let realCancelText = cancelText;
  if (realSubmitText == null) {
    realSubmitText = t('actions.save');
  }
  if (realCancelText == null) {
    realCancelText = t('actions.cancel');
  }

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      onClose();
    }
  };

  const paperStyles = {
    ...(PaperProps?.sx || {}),
  };

  return (
    <Dialog
      sx={[...(Array.isArray(sx) ? sx : [sx]), { overflow: 'hidden' }]}
      fullWidth
      maxWidth={maxWidth}
      fullScreen={effectiveFullScreen}
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      PaperProps={{ ...PaperProps, sx: paperStyles }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Typography variant="h6" component="span">
          {title}
        </Typography>
        <IconButton
          edge="end"
          color="inherit"
          onClick={onClose}
          aria-label="close"
          sx={{ ml: 2, color: 'text.secondary' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 3 }} {...ContentProps}>
        {children}
      </DialogContent>
      <DialogActions
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: extraActions ? 'space-between' : 'end',
          px: 3,
          py: 2,
          ...ActionsStyleProps,
        }}
      >
        {extraActions ? <Stack direction="row">{extraActions}</Stack> : null}

        <Stack direction="row" spacing={1}>
          {!hideCancel && (
            <Button variant="outlined" onClick={handleCancel}>
              {realCancelText}
            </Button>
          )}
          {onSubmit && (
            <Button
              variant="contained"
              color={submitColor}
              autoFocus
              disabled={isSubmitting || (canSubmit == null ? false : !canSubmit)}
              onClick={() => {
                if (isSubmitting) return;
                setIsSubmitting(true);
                requestAnimationFrame(() => {
                  onSubmit?.();
                  setIsSubmitting(false);
                });
              }}
            >
              {realSubmitText}
            </Button>
          )}
        </Stack>
      </DialogActions>
    </Dialog>
  );
}

export default ResponsiveDialog;
